<template>
  <div class="bg-white px-3 py-16">
    <div class="mx-auto max-w-3xl lg:max-w-5xl">
      <div class="mb-8 text-center">
        <JoszakiHeading :level="2" class="mb-3">
          {{ content.title }}
        </JoszakiHeading>
        <p
          v-if="content.subtitle || content.linkText"
          class="mx-auto max-w-4xl text-base leading-7 text-gray-600 lg:text-lg"
        >
          <span v-if="content.subtitle">{{ content.subtitle }}</span>
          <NuxtLink
            v-if="content.linkText"
            :to="content.linkUrl || localePath({ name: 'faqPage' })"
            class="ml-2 font-semibold text-primary no-underline hover:text-primary-darker"
          >
            {{ content.linkText }} &rarr;
          </NuxtLink>
        </p>
      </div>

      <div
        v-if="categories.length"
        class="mb-8 rounded-full border border-primary bg-white p-1 shadow-sm"
      >
        <div class="flex gap-2 overflow-x-auto">
          <button
            v-for="category in categories"
            :key="category.key"
            class="min-w-max rounded-full px-5 py-3 text-sm font-semibold transition-colors lg:text-base"
            :class="
              activeCategory === category.key
                ? 'bg-primary text-white shadow-sm'
                : 'text-gray-700 hover:bg-blue-50'
            "
            @click="activeCategory = category.key"
          >
            {{ category.label }}
          </button>
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <div
          v-for="(item, i) in visibleItems"
          :key="i"
          class="overflow-hidden rounded-lg border transition-all"
          :class="
            openItem === i
              ? 'border-primary bg-blue-50 shadow-sm'
              : 'border-gray-200 bg-white'
          "
        >
          <component
            :is="item.questionHeadingLevel || content.questionHeadingLevel || 'h3'"
            class="m-0"
          >
            <button
              class="flex w-full items-center justify-between px-5 py-4 text-left lg:px-6"
              @click="onToggleItem(i)"
            >
              <span
                class="pr-4 text-base font-semibold leading-6 lg:text-xl"
                :class="
                  openItem === i ? 'text-primary' : 'text-gray-800'
                "
              >
                {{ item.question }}
              </span>
              <IconComponent
                :icon="openItem === i ? 'angle-up' : 'angle-down'"
                class="ml-4 flex-shrink-0 text-gray-400"
              />
            </button>
          </component>
          <div v-if="openItem === i" class="px-5 pb-5 lg:px-6">
            <JoszakiHtmlTextBlock
              v-if="item.answer"
              :text="item.answer"
              class="mb-4 text-base leading-7 text-gray-600"
            />
            <JoszakiButton
              v-if="item.linkUrl"
              size="sm"
              :to="item.linkUrl"
            >
              Továbbiak &rarr;
            </JoszakiButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ref,
  computed,
  onMounted,
  watch,
  useContext,
} from "@nuxtjs/composition-api";
import useModuleContent from "~/composables/useModuleContent";
import JoszakiHtmlTextBlock from "~/components/_refactored/common/JoszakiHtmlTextBlock.vue";

export default {
  name: "FaqModule",
  components: {
    JoszakiHtmlTextBlock,
  },
  props: {
    moduleConfig: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const { $posthog, localePath } = useContext();
    const { content } = useModuleContent(props);

    const openItem = ref(0);
    const activeCategory = ref("");

    const allItems = computed(() => content.value.items || []);
    const categories = computed(() => {
      if (content.value.categories?.length) {
        return content.value.categories;
      }

      return [...new Set(allItems.value.map((item) => item.category).filter(Boolean))]
        .map((key) => ({ key, label: key }));
    });

    const visibleItems = computed(() => {
      if (!activeCategory.value) {
        return allItems.value;
      }

      return allItems.value.filter(
        (item) => item.category === activeCategory.value
      );
    });

    watch(
      categories,
      (newCategories) => {
        if (!newCategories.length) {
          activeCategory.value = "";
          return;
        }

        const hasActiveCategory = newCategories.some(
          (category) => category.key === activeCategory.value
        );

        if (!hasActiveCategory) {
          activeCategory.value = newCategories[0].key;
        }
      },
      { immediate: true }
    );

    watch(
      visibleItems,
      (newItems) => {
        if (!newItems.length) {
          openItem.value = null;
          return;
        }

        if (openItem.value === null || openItem.value >= newItems.length) {
          openItem.value = 0;
        }
      },
      { immediate: true }
    );

    const onToggleItem = (index) => {
      if (openItem.value === index) {
        openItem.value = null;
      } else {
        openItem.value = index;
        $posthog?.capture("front_page_faq_item_opened", {
          item_index: index,
          module_slug: props.moduleConfig.slug,
        });
      }
    };

    onMounted(() => {
      $posthog?.capture("front_page_module_viewed", {
        module_slug: props.moduleConfig.slug,
        module_position: props.moduleConfig.position,
        module_row: props.moduleConfig.row,
        version_id: props.moduleConfig.versionId,
      });
    });

    return {
      content,
      localePath,
      openItem,
      allItems,
      categories,
      activeCategory,
      visibleItems,
      onToggleItem,
    };
  },
};
</script>
